import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Badge } from '../../../components/ui/Badge';
import { Input } from '../../../components/ui/Input';
import {
  SelectRoot,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/Select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/Table';
import { Skeleton } from '../../../components/ui/Skeleton';
import {
  FileText,
  Search,
  Download,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSecurityAuditLog, AuditLogEntry } from '../../../hooks/useFinancialSecurity';

const ENTITY_TYPES = [
  { value: 'CUSTODY', label: 'Custódia' },
  { value: 'POR', label: 'Proof of Reserve' },
  { value: 'YIELD', label: 'Yield' },
  { value: 'POLICY', label: 'Política' },
];

const RESULTS = [
  { value: 'SUCCESS', label: 'Sucesso' },
  { value: 'FAILURE', label: 'Falha' },
];

export function SecurityAuditLog() {
  const [filters, setFilters] = useState({
    entityType: '',
    assetId: '',
    result: '',
    skip: 0,
    take: 20,
  });

  const { data, isLoading } = useSecurityAuditLog({
    entityType: filters.entityType || undefined,
    assetId: filters.assetId || undefined,
    result: filters.result || undefined,
    skip: filters.skip,
    take: filters.take,
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, skip: 0 }));
  };

  const handlePageChange = (direction: 'prev' | 'next') => {
    setFilters((prev) => ({
      ...prev,
      skip: direction === 'next' ? prev.skip + prev.take : Math.max(0, prev.skip - prev.take),
    }));
  };

  const clearFilters = () => {
    setFilters({
      entityType: '',
      assetId: '',
      result: '',
      skip: 0,
      take: 20,
    });
  };

  const exportLogs = () => {
    if (!data?.logs) return;

    const csv = [
      ['Data', 'Usuário', 'Ação', 'Tipo', 'Ativo', 'Resultado', 'Detalhes'].join(','),
      ...data.logs.map((log) =>
        [
          format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm:ss'),
          log.userName,
          log.action,
          log.entityType,
          log.assetName || '-',
          log.result,
          JSON.stringify(log.details).replace(/,/g, ';'),
        ].join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-log-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      CUSTODY_VALIDATION_DEPLOY: 'Validação de Custódia (Deploy)',
      CUSTODY_VALIDATION_MINT: 'Validação de Custódia (Mint)',
      POR_UPDATE_MINT: 'Atualização PoR (Mint)',
      POR_UPDATE_BURN: 'Atualização PoR (Burn)',
      POR_UPDATE_MANUAL: 'Atualização PoR (Manual)',
      YIELD_PAYMENT: 'Pagamento de Yield',
      POLICY_CHANGE_GLOBAL: 'Alteração de Política Global',
      POLICY_CHANGE_TENANT: 'Alteração de Política do Tenant',
    };
    return labels[action] || action;
  };

  const totalPages = data ? Math.ceil(data.total / filters.take) : 0;
  const currentPage = Math.floor(filters.skip / filters.take) + 1;

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Log de Auditoria</h1>
            <p className="text-muted-foreground">
              Histórico de operações de segurança financeira
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={exportLogs} disabled={!data?.logs?.length}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            <CardTitle className="text-sm">Filtros</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="w-48">
              <SelectRoot
                value={filters.entityType}
                onValueChange={(value) => handleFilterChange('entityType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Tipo de Entidade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos</SelectItem>
                  {ENTITY_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>

            <div className="w-48">
              <SelectRoot
                value={filters.result}
                onValueChange={(value) => handleFilterChange('result', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Resultado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">Todos</SelectItem>
                  {RESULTS.map((result) => (
                    <SelectItem key={result.value} value={result.value}>
                      {result.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
            </div>

            <div className="w-64">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="ID do Ativo"
                  value={filters.assetId}
                  onChange={(e) => handleFilterChange('assetId', e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {(filters.entityType || filters.result || filters.assetId) && (
              <Button variant="ghost" onClick={clearFilters}>
                Limpar Filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : data?.logs && data.logs.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Ativo</TableHead>
                    <TableHead>Resultado</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.logs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="whitespace-nowrap">
                        {format(new Date(log.createdAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{log.userName}</p>
                          <p className="text-xs text-muted-foreground">{log.userId.slice(0, 8)}...</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{getActionLabel(log.action)}</p>
                          <Badge variant="outline" className="text-xs">
                            {log.entityType}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        {log.assetName ? (
                          <span className="font-medium">{log.assetName}</span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={log.result === 'SUCCESS' ? 'default' : 'destructive'}
                          className="gap-1"
                        >
                          {log.result === 'SUCCESS' ? (
                            <CheckCircle2 className="h-3 w-3" />
                          ) : (
                            <XCircle className="h-3 w-3" />
                          )}
                          {log.result === 'SUCCESS' ? 'Sucesso' : 'Falha'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {log.details && Object.keys(log.details).length > 0 ? (
                          <details className="cursor-pointer">
                            <summary className="text-sm text-muted-foreground hover:text-foreground">
                              Ver detalhes
                            </summary>
                            <pre className="mt-2 text-xs bg-muted p-2 rounded max-w-xs overflow-auto">
                              {JSON.stringify(log.details, null, 2)}
                            </pre>
                          </details>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-muted-foreground">
                  Mostrando {filters.skip + 1} - {Math.min(filters.skip + filters.take, data.total)} de {data.total} registros
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange('prev')}
                    disabled={filters.skip === 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Anterior
                  </Button>
                  <span className="text-sm">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange('next')}
                    disabled={filters.skip + filters.take >= data.total}
                  >
                    Próxima
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>Nenhum registro encontrado</p>
              {(filters.entityType || filters.result || filters.assetId) && (
                <Button variant="link" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default SecurityAuditLog;
